// pages/admin/AdminPages.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChefHat,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Copy,
  Download,
  ExternalLink,
  ImagePlus,
  Lock,
  LogOut,
  Monitor,
  Pencil,
  Plus,
  QrCode,
  Radio,
  RefreshCw,
  Search,
  Smartphone,
  Store,
  Ticket,
  Trash2,
  Truck,
  Upload,
  UtensilsCrossed,
  Wine,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { AdminAiCard } from "@/components/AdminAiCard";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { ReleaseStatusPanel } from "@/components/ReleaseStatusPanel";
import { AdminErrorLogPanel } from "@/components/AdminErrorLogPanel";
import { setStoredDeviceRole } from "@/lib/device";
import { API, formatPrice } from "@/app/core";
import { clearAdminSession, readAdminSession, writeAdminSession } from "@/lib/authStorage";
import { armLoginChime, unlockAudio, useLoginChimeOnMount } from "@/app/chimes";
import { FullscreenToggleButton, ThemeToggleButton } from "@/app/fullscreen";
import { EmptyState, NfcWriteDialog, resizeImageFile, SectionHeader, ShareableLinkCard, TagQrPreview } from "@/app/fragments";


// =============================================================================
// Platform Admin (Super-Admin) — separate auth, restricted business management
// =============================================================================

const adminHttp = axios.create({ baseURL: API });

// Admin sessions are strictly tab-scoped (never persisted to disk) —
// see lib/authStorage.js for the storage policy.
const getAdminSession = () => readAdminSession();
const setAdminSession = (payload) => writeAdminSession(payload);

export function AdminLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const s = getAdminSession();
    if (s?.token) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Unlock the shared AudioContext inside this real user gesture so
    // the login chime + admin notification sounds work after navigation.
    unlockAudio();
    if (!username.trim() || !password.trim()) {
      toast.error("Enter username and password");
      return;
    }
    setSubmitting(true);
    try {
      const response = await adminHttp.post("/admin/login", {
        username: username.trim(),
        password,
      });
      setAdminSession(response.data);
      // Persist device role for PWA "remember device" — next launch can
      // jump straight to admin login if this device was configured as admin.
      try {
        setStoredDeviceRole("admin");
      } catch (_) { /* localStorage may be disabled */ }
      armLoginChime();
      navigate("/admin/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[radial-gradient(1200px_circle_at_20%_0%,hsl(var(--accent)/0.18),transparent_55%),radial-gradient(900px_circle_at_90%_10%,hsl(var(--primary)/0.14),transparent_50%)] px-4 py-6 sm:px-6 sm:py-10">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggleButton />
      </div>
      {/* Session 27.20 — Simplified Platform Admin login. The marketing
          block ("Manage every restaurant on the platform" + feature
          cards) was removed at owner request to match the clean
          single-card layout used by the other dashboards. */}
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-md items-center justify-center">
        <Card className="w-full rounded-[28px] border-border bg-card p-1 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-2xl">Admin login</CardTitle>
            <CardDescription>Restricted access for the platform operator.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="admin-username">Username</Label>
                <Input
                  id="admin-username"
                  className="h-11 rounded-xl bg-background"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  className="h-11 rounded-xl bg-background"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="h-11 w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                data-testid="admin-login-submit-button"
              >
                {submitting ? "Signing in..." : "Continue"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function useAdminAuth() {
  const navigate = useNavigate();
  const [session, setSessionState] = useState(() => getAdminSession());
  useEffect(() => {
    if (!session?.token) navigate("/admin", { replace: true });
  }, [session, navigate]);
  const headers = useMemo(() => (session?.token ? { Authorization: `Bearer ${session.token}` } : null), [session]);
  const logout = useCallback(async () => {
    const hSnapshot = headers;
    clearAdminSession();
    setSessionState(null);
    navigate("/admin", { replace: true });
    try { if (hSnapshot) await adminHttp.post("/admin/logout", {}, { headers: hSnapshot }); } catch (_) { /* ignore */ }
  }, [headers, navigate]);
  return { session, headers, logout };
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  useLoginChimeOnMount();
  const { session, headers, logout } = useAdminAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", slug: "", owner_password: "", staff_password: "", kitchen_password: "", bar_password: "", void_password: "", tagline: "" });
  const [creating, setCreating] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(null);
  // "Business install" — admin picks a business, the dialog shows the
  // one-tap install URL and a downloadable QR code so the admin can
  // WhatsApp / email either or both to the client.
  const [businessInstallDialog, setBusinessInstallDialog] = useState({ open: false });
  // Session 27.55 — App download management. The admin uploads an APK
  // and/or an EXE straight to the backend from this dialog, replacing
  // the old "Install as PWA" prompt entirely. The status is fetched
  // whenever the install dialog opens so a freshly-uploaded build is
  // reflected without a hard refresh.
  const [appDownloads, setAppDownloads] = useState({ apk: null, exe: null, loading: false });
  const [uploadingPlatform, setUploadingPlatform] = useState(""); // "apk" | "exe" | ""
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100
  const refreshAppDownloads = useCallback(async () => {
    if (!session?.token) return;
    setAppDownloads((s) => ({ ...s, loading: true }));
    try {
      const r = await adminHttp.get("/admin/downloads/status", {
        headers: { Authorization: `Bearer ${session.token}` },
      });
      setAppDownloads({ apk: r.data.apk || null, exe: r.data.exe || null, loading: false });
    } catch (e) {
      setAppDownloads((s) => ({ ...s, loading: false }));
    }
  }, [session?.token]);
  useEffect(() => {
    if (businessInstallDialog.open) refreshAppDownloads();
  }, [businessInstallDialog.open, refreshAppDownloads]);
  const handleUploadAppBuild = useCallback(async (platform, file) => {
    if (!file || !session?.token) return;
    // Enforce the same 200 MB cap on the client so users get instant
    // feedback instead of waiting for the server to reject.
    if (file.size > 200 * 1024 * 1024) {
      toast.error("File exceeds the 200 MB limit.");
      return;
    }
    setUploadingPlatform(platform);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await adminHttp.post(`/admin/downloads/upload/${platform}`, formData, {
        headers: {
          Authorization: `Bearer ${session.token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
        // Uploads can take longer than the default 20s http timeout
        // (a 40 MB APK on a slow connection = well over a minute).
        timeout: 5 * 60 * 1000,
      });
      toast.success(`${platform.toUpperCase()} uploaded (${(r.data.size_bytes / (1024 * 1024)).toFixed(1)} MB).`);
      await refreshAppDownloads();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Upload failed for ${platform.toUpperCase()}.`);
    } finally {
      setUploadingPlatform("");
      setUploadProgress(0);
    }
  }, [session?.token, refreshAppDownloads]);
  const handleDeleteAppBuild = useCallback(async (platform) => {
    if (!session?.token) return;
    if (!window.confirm(`Remove the uploaded ${platform.toUpperCase()} build? Clients won't be able to download it until you upload a new one.`)) return;
    try {
      await adminHttp.delete(`/admin/downloads/${platform}`, {
        headers: { Authorization: `Bearer ${session.token}` },
      });
      toast.success(`${platform.toUpperCase()} removed.`);
      await refreshAppDownloads();
    } catch (e) {
      toast.error(`Couldn't remove ${platform.toUpperCase()}.`);
    }
  }, [session?.token, refreshAppDownloads]);

  // Regenerate the QR image whenever the picked business changes. Doing
  // this in an effect (instead of inline in onValueChange) makes the
  // flow resilient to double-renders and lets us surface any encoder
  // error to the user instead of silently failing.
  // Session 27.68 — Removed the per-business "install link + QR"
  // section from the App downloads dialog. Clients now sign in by
  // entering their business code on the shared install page, so a
  // per-business link is redundant. The QR generation useEffect and
  // its state fields (businessId / qrDataUrl / qrError) were removed
  // along with the corresponding JSX block below.
  // Destructive action: dedicated dialog + typed-slug confirmation so an
  // admin can't wipe a venue with a stray click.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmSlug, setDeleteConfirmSlug] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Edit business — name + slug + tagline. Slug rename is allowed but
  // warned ("baked into NFC tags / shared URLs") so admins coordinate
  // with the venue before changing it.
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", slug: "", tagline: "" });
  const [editing, setEditing] = useState(false);

  const handleEditBusiness = async () => {
    if (!editTarget) return;
    const name = (editForm.name || "").trim();
    const slug = (editForm.slug || "").trim().toLowerCase();
    if (!name) { toast.error("Business name cannot be empty."); return; }
    if (!slug) { toast.error("Slug cannot be empty."); return; }
    setEditing(true);
    try {
      const payload = { name, slug, tagline: (editForm.tagline || "").trim() };
      await adminHttp.patch(`/admin/businesses/${editTarget.id}/branding`, payload, { headers });
      toast.success(`${name} updated.`);
      setEditTarget(null);
      setEditForm({ name: "", slug: "", tagline: "" });
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update business");
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteBusiness = async () => {
    if (!deleteTarget || deleteConfirmSlug.trim() !== deleteTarget.slug) {
      toast.error(`Type "${deleteTarget?.slug || ''}" to confirm.`);
      return;
    }
    setDeleting(true);
    try {
      await adminHttp.delete(`/admin/businesses/${deleteTarget.id}`, { headers });
      toast.success(`${deleteTarget.name} deleted.`);
      setDeleteTarget(null);
      setDeleteConfirmSlug("");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete business");
    } finally {
      setDeleting(false);
    }
  };

  const refresh = useCallback(async () => {
    if (!headers) return;
    setLoading(true);
    try {
      const [bizRes, emailRes] = await Promise.all([
        adminHttp.get("/admin/businesses", { headers }),
        adminHttp.get("/admin/email-status", { headers }),
      ]);
      setBusinesses(bizRes.data.businesses || []);
      setEmailConfigured(!!emailRes.data?.configured);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load businesses");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { refresh(); }, [refresh]);

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const handleCreate = async () => {
    const payload = {
      name: createForm.name.trim(),
      slug: slugify(createForm.slug || createForm.name),
      owner_password: createForm.owner_password,
      staff_password: createForm.staff_password,
      kitchen_password: createForm.kitchen_password,
      tagline: createForm.tagline.trim(),
    };
    // Bar + void passwords are optional at creation: backend mints a
    // placeholder the owner can rotate later from the Access tab.
    if (createForm.bar_password) payload.bar_password = createForm.bar_password;
    if (createForm.void_password) payload.void_password = createForm.void_password;
    if (!payload.name || !payload.slug || !payload.owner_password || !payload.staff_password || !payload.kitchen_password) {
      toast.error("Fill in name, slug, and owner / staff / kitchen passwords");
      return;
    }
    const mins = [payload.owner_password, payload.staff_password, payload.kitchen_password];
    if (payload.bar_password) mins.push(payload.bar_password);
    if (payload.void_password) mins.push(payload.void_password);
    if (mins.some((p) => p && p.length < 4)) {
      toast.error("Passwords must be at least 4 characters");
      return;
    }
    setCreating(true);
    try {
      await adminHttp.post("/admin/businesses", payload, { headers });
      toast.success(`Created ${payload.name}`);
      setCreateOpen(false);
      setCreateForm({ name: "", slug: "", owner_password: "", staff_password: "", kitchen_password: "", bar_password: "", void_password: "", tagline: "" });
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create business");
    } finally {
      setCreating(false);
    }
  };

  if (!session?.token) return null;

  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:px-6 sm:py-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <header className="flex flex-col gap-4 rounded-[28px] border border-border bg-card p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <Badge className="bg-[hsl(var(--primary))] text-primary-foreground">Platform admin</Badge>
            <h1 className="mt-3 font-['Space_Grotesk'] text-3xl font-semibold tracking-tight">Welcome, {session.username}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {emailConfigured === null ? "Checking email…" : emailConfigured ? "Platform email is configured." : "Platform email is NOT configured. Set GMAIL_USER and GMAIL_APP_PASSWORD in backend .env to enable receipts."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ThemeToggleButton />
            <FullscreenToggleButton />
            <Button variant="outline" className="rounded-xl" onClick={refresh} data-testid="admin-refresh-button">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  data-testid="admin-actions-button"
                >
                  Actions
                  <ChevronDown className="ml-1 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl" data-testid="admin-actions-menu">
                <DropdownMenuItem onSelect={() => setTimeout(() => setCreateOpen(true), 0)} data-testid="admin-create-business-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Business
                </DropdownMenuItem>
                {/* Session 27.68 — Removed the "Admin install" menu
                    item. It was a personal PWA-install prompt for the
                    admin's own device, but the admin now runs from a
                    regular browser and doesn't need an installed app
                    shell. Keeping only the App downloads (APK/EXE/OTA)
                    manager and Logout. */}
                <DropdownMenuItem onSelect={() => setTimeout(() => setBusinessInstallDialog({ open: true }), 0)} data-testid="admin-business-install-button">
                  <Download className="mr-2 h-4 w-4" />
                  App downloads
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Session 30 — Same standalone destructive Logout button as
                the staff / kitchen / bar / runner dashboards (was buried
                in the Actions dropdown). */}
            <Button variant="outline" className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive" onClick={logout} data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {loading && businesses.length === 0 ? (
            <Card className="rounded-[24px] border-border bg-card md:col-span-2 xl:col-span-3"><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
          ) : null}
          {!loading && businesses.length === 0 ? (
            <div className="md:col-span-2 xl:col-span-3">
              <EmptyState icon={Store} title="No businesses yet" description="Add your first restaurant to get started." />
            </div>
          ) : null}
          {businesses.map((biz) => (
            <Card
              key={biz.id}
              className="cursor-pointer rounded-[24px] border-border bg-card transition hover:border-[hsl(var(--accent))]"
              onClick={() => navigate(`/admin/businesses/${biz.id}`)}
              data-testid={`admin-business-card-${biz.slug}`}
            >
              {biz.cover_image ? (
                <div className="overflow-hidden rounded-t-[24px]">
                  <img src={biz.cover_image} alt={biz.name} className="h-32 w-full object-cover" />
                </div>
              ) : null}
              <CardContent className="space-y-2 p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-['Space_Grotesk'] text-xl font-semibold">{biz.name}</p>
                    <p className="text-xs text-muted-foreground">/{biz.slug}</p>
                  </div>
                  <Badge variant="outline" className="rounded-full">{biz.menu_items} items</Badge>
                </div>
                {biz.tagline ? <p className="text-sm text-muted-foreground line-clamp-2">{biz.tagline}</p> : null}
                <div className="flex flex-wrap items-center gap-3 pt-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Ticket className="h-3 w-3" /> {biz.tags} tags</span>
                  <span className="inline-flex items-center gap-1"><UtensilsCrossed className="h-3 w-3" /> {biz.menu_items} items</span>
                </div>
                <div className="flex justify-end gap-1 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditTarget({ id: biz.id, name: biz.name, slug: biz.slug });
                      setEditForm({ name: biz.name || "", slug: biz.slug || "", tagline: biz.tagline || "" });
                    }}
                    data-testid={`admin-business-edit-${biz.slug}`}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-xl text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    onClick={(e) => {
                      // Don't bubble into the card's navigate-on-click.
                      e.stopPropagation();
                      setDeleteConfirmSlug("");
                      setDeleteTarget({ id: biz.id, name: biz.name, slug: biz.slug });
                    }}
                    data-testid={`admin-business-delete-${biz.slug}`}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* Session 47 — platform-wide system error log + email alerts */}
        <AdminErrorLogPanel token={session?.token} businesses={businesses} />
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmSlug(""); } }}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="admin-business-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete {deleteTarget?.name}?
            </DialogTitle>
            <DialogDescription>
              This removes the business and every record scoped to it — menu, orders, tags, specials, sides, departments, and sessions. This cannot be undone. Type the slug <span className="font-mono font-semibold">{deleteTarget?.slug}</span> below to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-slug">Type slug to confirm</Label>
            <Input
              id="confirm-slug"
              autoFocus
              className="h-11 rounded-xl font-mono"
              placeholder={deleteTarget?.slug || ""}
              value={deleteConfirmSlug}
              onChange={(e) => setDeleteConfirmSlug(e.target.value)}
              data-testid="admin-delete-confirm-slug"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setDeleteTarget(null); setDeleteConfirmSlug(""); }} disabled={deleting}>Cancel</Button>
            <Button
              className="rounded-xl bg-destructive text-white hover:bg-destructive/90"
              onClick={handleDeleteBusiness}
              disabled={deleting || deleteConfirmSlug.trim() !== deleteTarget?.slug}
              data-testid="admin-delete-confirm-button"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit business — admin-only dialog to rename a venue, change its
          slug (= business code in URLs and NFC tags), or update the tagline.
          Slug rename is allowed because it is a corrective action: e.g.
          when a venue rebrands. We warn the admin in the UI because all
          previously-shared links / NFC tags break after slug change. */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) { setEditTarget(null); setEditForm({ name: "", slug: "", tagline: "" }); } }}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="admin-business-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit {editTarget?.name}
            </DialogTitle>
            <DialogDescription>
              Update the business name, code (slug), or tagline. Renaming the slug changes every public URL and breaks existing NFC tags — only do this after coordinating with the venue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-biz-name">Business name</Label>
              <Input
                id="edit-biz-name"
                className="h-11 rounded-xl"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Harbor Bistro"
                data-testid="admin-edit-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-biz-slug">Slug (business code &amp; URL path)</Label>
              <Input
                id="edit-biz-slug"
                className="h-11 rounded-xl font-mono text-sm"
                value={editForm.slug}
                onChange={(e) => setEditForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                placeholder="e.g., harbor-bistro"
                data-testid="admin-edit-slug"
              />
              <p className="text-xs text-muted-foreground">Lowercase, alphanumeric, hyphens only. Used in /owner, /staff, /kitchen, /bar URLs and as the business code at login.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-biz-tagline">Tagline <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input
                id="edit-biz-tagline"
                className="h-11 rounded-xl"
                value={editForm.tagline}
                onChange={(e) => setEditForm((f) => ({ ...f, tagline: e.target.value }))}
                placeholder="Short marketing line"
                data-testid="admin-edit-tagline"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setEditTarget(null); setEditForm({ name: "", slug: "", tagline: "" }); }} disabled={editing}>Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={handleEditBusiness}
              disabled={editing || !editForm.name.trim() || !editForm.slug.trim()}
              data-testid="admin-edit-confirm-button"
            >
              {editing ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── App downloads dialog ────────────────────────────────────────
         Session 27.68 — Simplified to just show the APK + EXE build
         cards and the OTA bundle manager. The old per-business
         "install link + QR code" flow was removed: clients now enter
         their business code on the shared install page, so a
         per-business link is no longer needed. Same code, one dialog. */}
      <Dialog open={businessInstallDialog.open} onOpenChange={(o) => setBusinessInstallDialog({ open: o })}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-[24px] sm:max-w-lg" data-testid="admin-business-install-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Space_Grotesk'] text-2xl">
              <Download className="h-5 w-5" />
              App downloads
            </DialogTitle>
            <DialogDescription>
              Upload the compiled APK / EXE builds and manage over-the-air update bundles. Clients enter their business code when they open the app, so no per-business link is needed here.
            </DialogDescription>
          </DialogHeader>

          {/* Session 30.28 — Release status: what the last GitHub build
              did (building / published / skipped / failed + why). Fed by
              the workflows via /api/ci/builds/report. */}
          <ReleaseStatusPanel token={session?.token} active={businessInstallDialog.open} />

          {/* Session 27.55 — App download management. The old "Install
              as web app" flow is retired; clients now download either
              the Android APK or the Windows EXE straight from the
              backend. This card shows what's currently uploaded and
              lets the admin replace / delete either build. Public
              download URLs (below each card) can be pasted into
              WhatsApp / SMS to distribute the build directly. */}
          <div className="rounded-2xl border border-border bg-secondary/30 p-4" data-testid="admin-app-downloads-panel">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">App builds</p>
                <p className="text-xs text-muted-foreground">Upload the compiled APK / EXE. Clients tap the link to install — no store account needed.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-lg"
                onClick={refreshAppDownloads}
                disabled={appDownloads.loading}
                data-testid="admin-app-downloads-refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${appDownloads.loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {["apk", "exe"].map((platform) => {
                const info = appDownloads[platform];
                const origin = typeof window !== "undefined" ? window.location.origin : "";
                const publicUrl = `${origin}/api/downloads/${platform}`;
                const isUploading = uploadingPlatform === platform;
                return (
                  <div
                    key={platform}
                    className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3"
                    data-testid={`admin-app-download-card-${platform}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {platform === "apk" ? (
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            <Smartphone className="h-4 w-4" />
                          </span>
                        ) : (
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                            <Monitor className="h-4 w-4" />
                          </span>
                        )}
                        <div>
                          <p className="text-sm font-semibold">{platform === "apk" ? "Android APK" : "Windows EXE"}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {info?.available
                              ? `${(info.size_bytes / (1024 * 1024)).toFixed(1)} MB · Updated ${new Date(info.updated_at).toLocaleString()}`
                              : "Not uploaded yet"}
                          </p>
                        </div>
                      </div>
                      {info?.available ? (
                        <Badge className="rounded-full border border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 dark:text-emerald-300" data-testid={`admin-app-download-status-${platform}`}>
                          Live
                        </Badge>
                      ) : (
                        <Badge className="rounded-full border border-border bg-secondary text-[10px] text-muted-foreground" data-testid={`admin-app-download-status-${platform}`}>
                          Empty
                        </Badge>
                      )}
                    </div>

                    {isUploading ? (
                      <div className="space-y-1.5">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full rounded-full bg-[hsl(var(--accent))] transition-[width]"
                            style={{ width: `${uploadProgress}%` }}
                            data-testid={`admin-app-download-progress-${platform}`}
                          />
                        </div>
                        <p className="text-[11px] text-muted-foreground">Uploading… {uploadProgress}%</p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept={platform === "apk" ? ".apk,application/vnd.android.package-archive" : ".exe,application/octet-stream"}
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files && e.target.files[0];
                              if (f) handleUploadAppBuild(platform, f);
                              e.target.value = ""; // allow re-upload of same file
                            }}
                            data-testid={`admin-app-download-file-${platform}`}
                          />
                          <span className="inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-secondary/60">
                            <Upload className="h-3.5 w-3.5" />
                            {info?.available ? "Replace" : "Upload"}
                          </span>
                        </label>
                        {info?.available ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-lg text-xs"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(publicUrl);
                                  toast.success(`${platform.toUpperCase()} link copied.`);
                                } catch (_) {
                                  toast.error("Couldn't copy — select and copy manually.");
                                }
                              }}
                              data-testid={`admin-app-download-copy-${platform}`}
                            >
                              <Copy className="h-3.5 w-3.5" />
                              Copy link
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-lg text-xs"
                              onClick={() => window.open(publicUrl, "_blank", "noopener")}
                              data-testid={`admin-app-download-open-${platform}`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              Test
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => handleDeleteAppBuild(platform)}
                              data-testid={`admin-app-download-delete-${platform}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    )}
                    {info?.available ? (
                      <p className="truncate text-[10px] font-mono text-muted-foreground" title={publicUrl}>{publicUrl}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Session 30.29 — the "Live updates (OTA)" bundle manager card was
              removed from this dialog at the owner's request (Release status +
              App builds cover the release flow). The OTA endpoints and device
              updater remain in place; the handlers below are unused for now. */}

          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setBusinessInstallDialog({ open: false })} data-testid="admin-business-install-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>Add a new business</DialogTitle>
            <DialogDescription>Onboard a new restaurant in 30 seconds. The owner can log in immediately with the credentials below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="biz-name">Business name</Label>
              <Input id="biz-name" className="h-11 rounded-xl" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) }))} placeholder="e.g., Joe's Pizza Palace" data-testid="admin-new-name" />
              <p className="text-xs text-muted-foreground">The public name shown to customers, staff, and owners.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="biz-slug">URL slug (also the login "business code")</Label>
              <Input id="biz-slug" className="h-11 rounded-xl font-mono text-sm" value={createForm.slug} onChange={(e) => setCreateForm((f) => ({ ...f, slug: slugify(e.target.value) }))} placeholder="e.g., joes-pizza-palace" data-testid="admin-new-slug" />
              <p className="text-xs text-muted-foreground">Lowercase letters, numbers, and hyphens only. Staff &amp; owner type this when logging in (e.g. <span className="font-mono">harbor-bistro</span>). Auto-suggested from the name above — you can override it.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="biz-tagline">Tagline <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="biz-tagline" className="h-11 rounded-xl" value={createForm.tagline} onChange={(e) => setCreateForm((f) => ({ ...f, tagline: e.target.value }))} placeholder="e.g., Authentic woodfired pizza in 12 minutes" />
              <p className="text-xs text-muted-foreground">A short marketing one-liner shown on the dashboard card. Leave blank if unsure.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="biz-owner-pw">Owner password</Label>
                <Input id="biz-owner-pw" type="password" className="h-11 rounded-xl" value={createForm.owner_password} onChange={(e) => setCreateForm((f) => ({ ...f, owner_password: e.target.value }))} placeholder="min 4 characters" data-testid="admin-new-owner-password" />
                <p className="text-xs text-muted-foreground">Logs in at <span className="font-mono">/owner</span>.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="biz-staff-pw">Staff password</Label>
                <Input id="biz-staff-pw" type="password" className="h-11 rounded-xl" value={createForm.staff_password} onChange={(e) => setCreateForm((f) => ({ ...f, staff_password: e.target.value }))} placeholder="min 4 characters" data-testid="admin-new-staff-password" />
                <p className="text-xs text-muted-foreground">Logs in at <span className="font-mono">/staff</span>.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="biz-kitchen-pw">Kitchen password</Label>
                <Input id="biz-kitchen-pw" type="password" className="h-11 rounded-xl" value={createForm.kitchen_password} onChange={(e) => setCreateForm((f) => ({ ...f, kitchen_password: e.target.value }))} placeholder="min 4 characters" data-testid="admin-new-kitchen-password" />
                <p className="text-xs text-muted-foreground">Logs in at <span className="font-mono">/kitchen</span>.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="biz-bar-pw">Bar password <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="biz-bar-pw" type="password" className="h-11 rounded-xl" value={createForm.bar_password} onChange={(e) => setCreateForm((f) => ({ ...f, bar_password: e.target.value }))} placeholder="Leave blank to set later" data-testid="admin-new-bar-password" />
                <p className="text-xs text-muted-foreground">Logs in at <span className="font-mono">/bar</span>. Owners can rotate it on the Access tab.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="biz-void-pw">Void / manager password <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="biz-void-pw" type="password" className="h-11 rounded-xl" value={createForm.void_password} onChange={(e) => setCreateForm((f) => ({ ...f, void_password: e.target.value }))} placeholder="Leave blank to set later" data-testid="admin-new-void-password" />
                <p className="text-xs text-muted-foreground">Managers enter this to void orders or individual lines from the Staff dashboard.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={handleCreate}
              disabled={creating}
              data-testid="admin-confirm-create-button"
            >
              {creating ? "Creating…" : "Create business"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

export function AdminBusinessDetailPage() {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { session, headers, logout } = useAdminAuth();
  const [business, setBusiness] = useState(null);
  const [tab, setTab] = useState("manage-menu");
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [tags, setTags] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentDialog, setDepartmentDialog] = useState({
    open: false,
    mode: "create",
    values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, active: true },
  });
  const [adminDeptCategoryIds, setAdminDeptCategoryIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const [itemDialog, setItemDialog] = useState({ open: false, mode: "create", values: {} });
  const [tagDialog, setTagDialog] = useState({ open: false, mode: "create", values: {} });
  const [accessForm, setAccessForm] = useState({ owner_password: "", staff_password: "", kitchen_password: "", bar_password: "", void_password: "", runner_password: "", retail_password: "" });
  // Admin-only analytics-tab password. Sent to its own dedicated endpoint
  // (separate from the owner-login passwords) so owners can't see / change
  // it from their dashboard.
  const [analyticsPwInput, setAnalyticsPwInput] = useState("");
  const [analyticsPwSaving, setAnalyticsPwSaving] = useState(false);
  // Filters for the Manage Menu items view — same UX as the Owner dashboard:
  // category dropdown + free-text search.
  const [adminItemFilterCategory, setAdminItemFilterCategory] = useState("all");
  const [adminItemSearchTerm, setAdminItemSearchTerm] = useState("");
  // Branding tab — local draft so admins can preview before saving.
  const [brandingDraft, setBrandingDraft] = useState({
    name: "",
    cover_image: "",
    cover_image_fit: "cover",
  });
  const [brandingSaving, setBrandingSaving] = useState(false);
  // Admin-side Staff & Access — full mirror of the owner Staff & Access
  // panel so platform admins can list, create, edit, reset PINs and
  // delete user accounts on behalf of any business.
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoaded, setAdminUsersLoaded] = useState(false);
  const [adminUserDialog, setAdminUserDialog] = useState({ open: false, mode: "create", id: "", values: { first_name: "", last_name: "", username: "", pin: "", role: "waiter", active: true, allowed_sections: null } });
  const [adminPinDialog, setAdminPinDialog] = useState({ open: false, user: null, pin: "" });
  // Hard-delete user dialog on the admin dashboard. Requires the
  // business's Void Manager Password, mirroring the Owner dashboard
  // behaviour so the same authority is enforced regardless of where the
  // deletion originates. Allows ALL roles to be deleted (including
  // Owner; the server still blocks deleting the LAST active Owner).
  const [adminDeleteUserDialog, setAdminDeleteUserDialog] = useState({ open: false, user: null, pin: "", saving: false });
  const loadAdminUsers = useCallback(async () => {
    try {
      const r = await adminHttp.get(`/admin/businesses/${businessId}/users`, { headers });
      setAdminUsers(r.data.users || []);
      setAdminUsersLoaded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to load users.");
    }
  }, [businessId, headers]);
  const submitAdminUser = async () => {
    const v = adminUserDialog.values;
    if (!v.first_name.trim() || !v.username.trim()) { toast.error("First name + username required."); return; }
    if (adminUserDialog.mode === "create" && (!v.pin || v.pin.length < 4)) { toast.error("PIN must be at least 4 characters."); return; }
    const sectionsPayload = ["owner","manager","shift_leader","supervisor"].includes(v.role)
      ? (Array.isArray(v.allowed_sections) ? v.allowed_sections : [])
      : null;
    try {
      if (adminUserDialog.mode === "create") {
        await adminHttp.post(`/admin/businesses/${businessId}/users`, {
          first_name: v.first_name.trim(), last_name: v.last_name.trim(),
          username: v.username.trim(), pin: v.pin, role: v.role,
          active: !!v.active, allowed_sections: sectionsPayload,
        }, { headers });
        toast.success("User created.");
      } else {
        await adminHttp.patch(`/admin/businesses/${businessId}/users/${adminUserDialog.id}`, {
          first_name: v.first_name.trim(), last_name: v.last_name.trim(),
          username: v.username.trim(), role: v.role, active: !!v.active,
          allowed_sections: sectionsPayload,
        }, { headers });
        toast.success("User updated.");
      }
      setAdminUserDialog({ open: false, mode: "create", id: "", values: { first_name: "", last_name: "", username: "", pin: "", role: "waiter", active: true, allowed_sections: null } });
      await loadAdminUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to save user.");
    }
  };
  const resetAdminUserPin = async () => {
    if (!adminPinDialog.user || !adminPinDialog.pin || adminPinDialog.pin.length < 4) { toast.error("PIN must be at least 4 characters."); return; }
    try {
      await adminHttp.post(`/admin/businesses/${businessId}/users/${adminPinDialog.user.id}/reset-pin`, { pin: adminPinDialog.pin }, { headers });
      toast.success(`New PIN set for ${adminPinDialog.user.first_name}.`);
      setAdminPinDialog({ open: false, user: null, pin: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to reset PIN.");
    }
  };
  const toggleAdminUserActive = async (u) => {
    try {
      await adminHttp.patch(`/admin/businesses/${businessId}/users/${u.id}`, { active: !u.active }, { headers });
      await loadAdminUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to update user."); }
  };
  const deleteAdminUser = async (u) => {
    if (!window.confirm(`Delete user "${u.first_name} ${u.last_name}"? This cannot be undone.`)) return;
    try {
      await adminHttp.delete(`/admin/businesses/${businessId}/users/${u.id}`, { headers });
      toast.success("User deleted.");
      await loadAdminUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to delete user."); }
  };

  const submitAdminDeleteUser = async () => {
    const v = adminDeleteUserDialog;
    if (!v.user) return;
    if (!v.pin || v.pin.length < 4) { toast.error("Enter the Void Manager Password."); return; }
    setAdminDeleteUserDialog((d) => ({ ...d, saving: true }));
    try {
      await adminHttp.delete(`/admin/businesses/${businessId}/users/${v.user.id}`, { headers, data: { manager_pin: v.pin } });
      toast.success(`${v.user.first_name || "User"} deleted.`);
      setAdminDeleteUserDialog({ open: false, user: null, pin: "", saving: false });
      await loadAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete user.");
      setAdminDeleteUserDialog((d) => ({ ...d, saving: false }));
    }
  };

  const handleAdminCoverImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const optimized = await resizeImageFile(file);
      setBrandingDraft((d) => ({ ...d, cover_image: optimized }));
      toast.success("Cover image ready. Click Save branding to apply.");
    } catch (error) {
      toast.error(error.message || "Unable to process image.");
    } finally {
      event.target.value = "";
    }
  };

  const refresh = useCallback(async () => {
    if (!headers || !businessId) return;
    setLoading(true);
    try {
      const [bizRes, menuRes, tagsRes, deptRes] = await Promise.all([
        adminHttp.get(`/admin/businesses/${businessId}`, { headers }),
        adminHttp.get(`/admin/businesses/${businessId}/menu`, { headers }),
        adminHttp.get(`/admin/businesses/${businessId}/tags`, { headers }),
        adminHttp.get(`/admin/businesses/${businessId}/departments`, { headers }),
      ]);
      setBusiness(bizRes.data);
      // Sync the branding draft from the freshly-loaded business so admins
      // start editing from the current values (not the previous business's).
      setBrandingDraft({
        name: bizRes.data?.name || "",
        cover_image: bizRes.data?.cover_image || "",
        cover_image_fit: bizRes.data?.cover_image_fit || "cover",
      });
      const cats = menuRes.data.categories || [];
      const flatItems = [];
      cats.forEach((cat) => {
        (cat.items || []).forEach((it) => {
          flatItems.push({ ...it, category_id: cat.id });
        });
      });
      setMenu({ categories: cats.map((c) => ({ ...c, items: undefined })), items: flatItems });
      setTags(tagsRes.data.tags || []);
      setDepartments(deptRes.data.departments || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load business");
    } finally {
      setLoading(false);
    }
  }, [headers, businessId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSaveItem = async () => {
    const v = itemDialog.values;
    if (!v.name || !v.price || !v.category_id) {
      toast.error("Name, price, and category are required");
      return;
    }
    const payload = {
      name: v.name.trim(),
      description: (v.description || "").trim(),
      price: Number(v.price),
      image: v.image || null,
      category_id: v.category_id,
      vat_percent: Number(v.vat_percent ?? 15),
      department: v.department || "",
      featured: !!v.featured,
      active: v.active !== false,
      notes_mode: ["off", "staff", "all"].includes(v.notes_mode) ? v.notes_mode : "off",
    };
    try {
      if (itemDialog.mode === "create") {
        await adminHttp.post(`/admin/businesses/${businessId}/menu-items`, payload, { headers });
        toast.success("Item created");
      } else {
        await adminHttp.patch(`/admin/businesses/${businessId}/menu-items/${v.id}`, payload, { headers });
        toast.success("Item updated");
      }
      setItemDialog({ open: false, mode: "create", values: {} });
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Save failed");
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    try {
      await adminHttp.delete(`/admin/businesses/${businessId}/menu-items/${id}`, { headers });
      toast.success("Item deleted");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const handleAddCategory = async () => {
    const name = window.prompt("New category name?");
    if (!name?.trim()) return;
    try {
      await adminHttp.post(`/admin/businesses/${businessId}/categories`, { name: name.trim(), description: "", active: true }, { headers });
      toast.success("Category added");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Add failed");
    }
  };

  // Move an admin-side category up/down. Optimistic UI then persist the
  // full ordered ID list to the backend.
  const moveAdminCategory = async (index, direction) => {
    const next = [...(menu.categories || [])];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setMenu((m) => ({ ...m, categories: next }));
    try {
      await adminHttp.patch(
        `/admin/businesses/${businessId}/categories/reorder`,
        { ids: next.map((c) => c.id) },
        { headers },
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reorder.");
      refresh();
    }
  };

  const handleSaveTag = async () => {
    const v = tagDialog.values;
    if (!v.label || !v.label.trim()) {
      toast.error("Label is required");
      return;
    }
    const tableName = (v.label || "").trim();
    if (!tableName) {
      toast.error("Enter a table name or number.");
      return;
    }
    const payload = {
      label: tableName,
      type: "table",
      table_number: tableName,
      active: v.active !== false,
    };
    try {
      if (tagDialog.mode === "create") {
        await adminHttp.post(`/admin/businesses/${businessId}/tags`, payload, { headers });
        toast.success("Tag created");
      } else {
        await adminHttp.patch(`/admin/businesses/${businessId}/tags/${v.id}`, payload, { headers });
        toast.success("Tag updated");
      }
      setTagDialog({ open: false, mode: "create", values: {} });
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Save failed");
    }
  };

  const [nfcWriteTag, setNfcWriteTag] = useState(null);

  const handleAdminWriteNfc = (tag) => {
    if (!tag || !tag.path) {
      toast.error("This tag is missing a URL — try refreshing.");
      return;
    }
    setNfcWriteTag(tag);
  };

  const handleDeleteTag = async (id) => {
    if (!window.confirm("Delete this tag?")) return;
    try {
      await adminHttp.delete(`/admin/businesses/${businessId}/tags/${id}`, { headers });
      toast.success("Tag deleted");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const handleSaveDepartment = async () => {
    const v = departmentDialog.values;
    const name = (v.name || "").trim();
    if (!name) {
      toast.error("Department name is required.");
      return;
    }
    try {
      let savedId = v.id;
      if (departmentDialog.mode === "create") {
        const created = await adminHttp.post(
          `/admin/businesses/${businessId}/departments`,
          { name, route_to_kitchen: !!v.route_to_kitchen, route_to_bar: !!v.route_to_bar, route_to_staff: !!v.route_to_staff, active: v.active !== false },
          { headers },
        );
        savedId = created.data.department?.id;
        toast.success("Department created");
      } else {
        await adminHttp.patch(
          `/admin/businesses/${businessId}/departments/${v.id}`,
          { name, route_to_kitchen: !!v.route_to_kitchen, route_to_bar: !!v.route_to_bar, route_to_staff: !!v.route_to_staff, active: v.active !== false },
          { headers },
        );
        toast.success("Department updated");
      }
      if (savedId && adminDeptCategoryIds.length > 0) {
        try {
          const res = await adminHttp.post(
            `/admin/businesses/${businessId}/departments/${savedId}/assign-categories`,
            { category_ids: adminDeptCategoryIds },
            { headers },
          );
          if (res.data?.updated) {
            toast.success(`Reassigned ${res.data.updated} item(s) to "${name}".`);
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not bulk-assign categories.");
        }
      }
      setDepartmentDialog({ open: false, mode: "create", values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, route_to_staff: false, active: true } });
      setAdminDeptCategoryIds([]);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save department.");
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}"? Menu items must be reassigned first.`)) return;
    try {
      await adminHttp.delete(`/admin/businesses/${businessId}/departments/${dept.id}`, { headers });
      toast.success("Department deleted");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete department.");
    }
  };

  const handleUpdateAccess = async () => {
    const payload = {};
    if (accessForm.owner_password) payload.owner_password = accessForm.owner_password;
    if (accessForm.staff_password) payload.staff_password = accessForm.staff_password;
    if (accessForm.kitchen_password) payload.kitchen_password = accessForm.kitchen_password;
    if (accessForm.bar_password) payload.bar_password = accessForm.bar_password;
    if (accessForm.void_password) payload.void_password = accessForm.void_password;
    if (!Object.keys(payload).length) {
      toast.error("Enter at least one new password");
      return;
    }
    try {
      await adminHttp.post(`/admin/businesses/${businessId}/access`, payload, { headers });
      toast.success("Passwords updated");
      setAccessForm({ owner_password: "", staff_password: "", kitchen_password: "", bar_password: "", void_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    }
  };

  // Set or clear the analytics-tab gate password. Empty string removes the
  // gate entirely (admins disabling the lock for a venue).
  const handleSaveAnalyticsPassword = async () => {
    setAnalyticsPwSaving(true);
    try {
      await adminHttp.patch(
        `/admin/businesses/${businessId}/analytics-password`,
        { password: analyticsPwInput },
        { headers },
      );
      toast.success(analyticsPwInput.trim() ? "Analytics password updated." : "Analytics gate cleared.");
      setAnalyticsPwInput("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    } finally {
      setAnalyticsPwSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    const name = (brandingDraft.name || "").trim();
    if (!name) {
      toast.error("Business name is required.");
      return;
    }
    setBrandingSaving(true);
    try {
      const payload = {
        name,
        cover_image: brandingDraft.cover_image || "",
        cover_image_fit: brandingDraft.cover_image_fit || "cover",
      };
      const res = await adminHttp.patch(`/admin/businesses/${businessId}/branding`, payload, { headers });
      toast.success("Branding updated");
      if (res.data?.business) {
        setBusiness((b) => ({ ...(b || {}), ...res.data.business }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save branding");
    } finally {
      setBrandingSaving(false);
    }
  };

  if (!session?.token) return null;

  const itemsByCat = menu.categories.map((cat) => ({
    ...cat,
    items: menu.items.filter((it) => String(it.category_id) === String(cat.id)),
  }));

  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:px-6 sm:py-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <header className="flex flex-col gap-4 rounded-[28px] border border-border bg-card p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <Badge className="bg-[hsl(var(--primary))] text-primary-foreground">Admin · Restricted view</Badge>
            <h1 className="mt-3 font-['Space_Grotesk'] text-3xl font-semibold tracking-tight">{business?.name || "Loading…"}</h1>
            <p className="text-sm text-muted-foreground">/{business?.slug}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ThemeToggleButton />
            <FullscreenToggleButton />
            <Button variant="outline" className="rounded-xl" onClick={() => navigate("/admin/dashboard")} data-testid="admin-back-button">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button variant="outline" className="rounded-xl" onClick={logout}>
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        {/* Session 32 — per-business AI copilot: on/off, monthly budget, voice, usage. */}
        <AdminAiCard adminHttp={adminHttp} headers={headers} businessId={businessId} onEnabledChange={(v) => setBusiness((b) => ({ ...(b || {}), ai_assistant_enabled: v }))} />

        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "access" && !adminUsersLoaded) loadAdminUsers(); }}>
          <TabsList className="h-auto w-full flex-wrap justify-start gap-2 rounded-2xl bg-secondary p-2">
            {[
              { value: "manage-menu", label: "Manage Menu" },
              { value: "departments", label: "Departments" },
              { value: "tags", label: "Tags" },
              { value: "branding", label: "Branding" },
              { value: "links", label: "Links" },
              { value: "access", label: "Staff & Access" },
            ].map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="rounded-xl px-4 py-2" data-testid={`admin-tab-${t.value}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="manage-menu" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SectionHeader eyebrow="Menu" title="Categories & items" description="Add or update items on behalf of this business." />
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-xl" onClick={handleAddCategory} data-testid="admin-add-category-button">
                  <Plus className="h-4 w-4" />
                  Add Category
                </Button>
                <Button
                  className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  onClick={() => setItemDialog({ open: true, mode: "create", values: { vat_percent: 15, department: departments[0]?.name || "", active: true, category_id: menu.categories[0]?.id || "" } })}
                  disabled={!menu.categories.length}
                  data-testid="admin-add-item-button"
                >
                  <Plus className="h-4 w-4" />
                  Add Item
                </Button>
              </div>
            </div>
            {loading && !itemsByCat.length ? (
              <Card className="rounded-[24px] border-border bg-card"><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
            ) : null}
            {!loading && !menu.categories.length ? (
              <EmptyState icon={UtensilsCrossed} title="No categories yet" description="Add a category before creating menu items." />
            ) : null}
            {menu.categories.length ? (() => {
              const normalizedSearch = adminItemSearchTerm.trim().toLowerCase();
              // First filter categories by the category dropdown.
              let visibleCats = itemsByCat;
              if (adminItemFilterCategory !== "all") {
                visibleCats = itemsByCat.filter((c) => String(c.id) === String(adminItemFilterCategory));
              }
              // Then filter items inside each visible category by the search term.
              if (normalizedSearch) {
                visibleCats = visibleCats.map((cat) => ({
                  ...cat,
                  items: cat.items.filter((it) => {
                    const hay = [it.name || "", it.description || "", cat.name || "", it.department || ""].join(" ").toLowerCase();
                    return hay.includes(normalizedSearch);
                  }),
                }));
              }
              const totalItems = menu.items.length;
              const shownItems = visibleCats.reduce((sum, c) => sum + c.items.length, 0);
              const filtersActive = adminItemFilterCategory !== "all" || !!adminItemSearchTerm;
              const selectedCatName = adminItemFilterCategory !== "all"
                ? (menu.categories.find((c) => String(c.id) === String(adminItemFilterCategory))?.name || "selected category")
                : "";
              return (
                <>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-testid="admin-menu-filters">
                    <div className="relative flex-1">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={adminItemSearchTerm}
                        onChange={(e) => setAdminItemSearchTerm(e.target.value)}
                        placeholder="Search items by name, description or department…"
                        className="h-11 rounded-xl bg-background pl-10"
                        data-testid="admin-menu-items-search-input"
                      />
                    </div>
                    <Select value={adminItemFilterCategory} onValueChange={setAdminItemFilterCategory}>
                      <SelectTrigger className="h-11 w-full rounded-xl sm:w-64" data-testid="admin-menu-items-category-filter">
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {menu.categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="admin-menu-items-result-count">
                    Showing {shownItems} of {totalItems} item{totalItems === 1 ? "" : "s"}
                    {selectedCatName ? ` in ${selectedCatName}` : ""}
                    {adminItemSearchTerm ? ` matching "${adminItemSearchTerm}"` : ""}.
                  </p>
                  {visibleCats.map((cat) => {
                    const realIdx = (menu.categories || []).findIndex((c) => c.id === cat.id);
                    const isFirst = realIdx <= 0;
                    const isLast = realIdx >= (menu.categories || []).length - 1;
                    return (
              <Card key={cat.id} className="rounded-[24px] border-border bg-card">
                <CardContent className="space-y-3 p-5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-['Space_Grotesk'] text-lg font-semibold">{cat.name}</p>
                      <p className="text-xs text-muted-foreground">{cat.items.length} items</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 rounded-xl"
                        onClick={() => moveAdminCategory(realIdx, -1)}
                        disabled={isFirst}
                        aria-label={`Move ${cat.name} up`}
                        data-testid={`admin-category-up-${cat.id}`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 rounded-xl"
                        onClick={() => moveAdminCategory(realIdx, 1)}
                        disabled={isLast}
                        aria-label={`Move ${cat.name} down`}
                        data-testid={`admin-category-down-${cat.id}`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {cat.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground sm:col-span-2">
                        {normalizedSearch ? `No items match "${adminItemSearchTerm}" in this category.` : "No items in this category yet."}
                      </p>
                    ) : null}
                    {cat.items.map((it) => (
                      <div key={it.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 p-3">
                        <div className="min-w-0">
                          <p className="truncate font-semibold">{it.name}</p>
                          <p className="text-xs text-muted-foreground">{formatPrice(it.price)} · VAT {it.vat_percent ?? 15}%</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="icon" variant="ghost" className="h-9 w-9 rounded-lg" onClick={() => setItemDialog({ open: true, mode: "edit", values: { ...it } })} data-testid={`admin-edit-item-${it.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-9 w-9 rounded-lg text-destructive" onClick={() => handleDeleteItem(it.id)} data-testid={`admin-delete-item-${it.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
                  );
                  })}
                  {visibleCats.length === 0 ? (
                    <EmptyState
                      icon={Search}
                      title="No matching items"
                      description={`No items found${selectedCatName ? ` in ${selectedCatName}` : ""}${adminItemSearchTerm ? ` matching "${adminItemSearchTerm}"` : ""}. Try adjusting your filters.`}
                    />
                  ) : null}
                </>
              );
            })() : null}
          </TabsContent>

          <TabsContent value="tags" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SectionHeader eyebrow="QR / NFC" title="Tags" description="Each tag opens the menu (or a specific table) for customers." />
              <Button
                className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                onClick={() => setTagDialog({ open: true, mode: "create", values: { active: true } })}
                data-testid="admin-add-tag-button"
              >
                <Plus className="h-4 w-4" />
                Add Tag
              </Button>
            </div>
            {tags.length === 0 ? (
              <EmptyState icon={Ticket} title="No tags yet" description="Create a menu tag or table tag for this business." />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {tags.filter((tag) => !tag.is_preview).map((tag) => (
                  <Card key={tag.id} className="rounded-[24px] border-border bg-card">
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-['Space_Grotesk'] text-lg font-semibold">{tag.label}</p>
                          <p className="text-xs text-muted-foreground">Menu tag</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="icon" variant="ghost" className="h-9 w-9 rounded-lg" onClick={() => setTagDialog({ open: true, mode: "edit", values: { ...tag } })} data-testid={`admin-edit-tag-${tag.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-9 w-9 rounded-lg text-destructive" onClick={() => handleDeleteTag(tag.id)} data-testid={`admin-delete-tag-${tag.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="break-all rounded-xl bg-secondary/50 px-3 py-2 font-mono text-xs text-muted-foreground">{`${window.location.origin}${tag.path}`}</p>
                      <div className="flex items-start gap-3">
                        <TagQrPreview
                          url={`${window.location.origin}${tag.path}`}
                          filename={`qr-${tag.label || tag.code || tag.id}`}
                          size={140}
                          testIdPrefix={`admin-tags-qr-${tag.id}`}
                        />
                        <p className="flex-1 text-xs text-muted-foreground">
                          QR code uses the same single-use token flow as the NFC tap. Click <span className="font-semibold text-foreground">Save QR image</span> to download a printable PNG.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={tag.active ? "default" : "outline"}>{tag.active ? "Active" : "Inactive"}</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-auto rounded-lg"
                          onClick={() => handleAdminWriteNfc(tag)}
                          data-testid={`admin-write-nfc-${tag.id}`}
                        >
                          <Radio className="h-4 w-4" />
                          Write to NFC
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="departments" className="space-y-4">
            <SectionHeader
              eyebrow="Workflow"
              title="Departments"
              description="Departments group menu items by station. Items in a department flagged 'Routes to kitchen' show up on the Kitchen Dashboard."
              action={
                <Button
                  className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  onClick={() => { setAdminDeptCategoryIds([]); setDepartmentDialog({ open: true, mode: "create", values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, route_to_staff: false, active: true } }); }}
                  data-testid="admin-add-department-button"
                >
                  <Plus className="h-4 w-4" />
                  Add department
                </Button>
              }
            />
            {departments.length === 0 ? (
              <EmptyState
                icon={ChefHat}
                title="No departments yet"
                description="Create the first department for this business so menu items can be routed to the right station."
              />
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {departments.map((dept) => (
                  <Card key={dept.id} className="rounded-[24px] border-border bg-card">
                    <CardContent className="space-y-3 p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-['Space_Grotesk'] text-lg font-semibold">{dept.name}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            {dept.route_to_kitchen ? (
                              <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 dark:bg-emerald-500">
                                <ChefHat className="h-3 w-3" />
                                Routes to kitchen
                              </Badge>
                            ) : (
                              <Badge variant="outline">Front of house</Badge>
                            )}
                            {dept.active === false ? (
                              <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {(menu.items || []).filter((it) => (it.department || "").toLowerCase() === (dept.name || "").toLowerCase()).length} menu item(s)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-xl"
                          onClick={() => { setAdminDeptCategoryIds([]); setDepartmentDialog({ open: true, mode: "edit", values: { id: dept.id, name: dept.name, route_to_kitchen: !!dept.route_to_kitchen, route_to_bar: !!dept.route_to_bar, route_to_staff: !!dept.route_to_staff, active: dept.active !== false } }); }}
                          data-testid={`admin-edit-department-${dept.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDeleteDepartment(dept)}
                          data-testid={`admin-delete-department-${dept.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="branding" className="space-y-4">
            <SectionHeader
              eyebrow="Look & feel"
              title="Branding"
              description="Set the public-facing identity for this business — the customer menu hero and business name."
            />
            <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
              <Card className="rounded-[24px] border-border bg-card">
                <CardContent className="space-y-4 p-5">
                  <div className="space-y-2">
                    <Label htmlFor="admin-brand-name">Business name</Label>
                    <Input
                      id="admin-brand-name"
                      className="h-11 rounded-xl"
                      value={brandingDraft.name}
                      onChange={(e) => setBrandingDraft((d) => ({ ...d, name: e.target.value }))}
                      placeholder="e.g. Harbor Bistro"
                      maxLength={120}
                      data-testid="admin-branding-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cover image</Label>
                    {brandingDraft.cover_image ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Label className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-secondary">
                          <ImagePlus className="h-4 w-4" />
                          Replace image
                          <Input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleAdminCoverImageChange}
                            data-testid="admin-branding-cover-upload"
                          />
                        </Label>
                        <Button
                          variant="outline"
                          className="h-11 rounded-xl text-destructive"
                          onClick={() => setBrandingDraft((d) => ({ ...d, cover_image: "" }))}
                          data-testid="admin-branding-cover-remove"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <Label className="flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground hover:bg-secondary/60">
                        <ImagePlus className="h-6 w-6" />
                        <span><strong className="text-foreground">Click to upload</strong> a cover image</span>
                        <span className="text-xs">JPG or PNG · banner shape works best (e.g. 1500 × 500&nbsp;px)</span>
                        <Input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleAdminCoverImageChange}
                          data-testid="admin-branding-cover-upload"
                        />
                      </Label>
                    )}
                    <p className="text-xs text-muted-foreground">
                      The customer menu displays the cover as a wide banner (3:1 ratio). Design at <strong className="text-foreground">~1500 × 500&nbsp;px</strong> for a perfect fit. Anything bigger gets auto-shrunk to keep menus fast.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Image fit</Label>
                    <Select value={brandingDraft.cover_image_fit} onValueChange={(v) => setBrandingDraft((d) => ({ ...d, cover_image_fit: v }))}>
                      <SelectTrigger className="h-11 rounded-xl" data-testid="admin-branding-cover-fit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cover">Cover — crop to fill (best for photography)</SelectItem>
                        <SelectItem value="contain">Contain — letterbox the full image (best for logos)</SelectItem>
                        <SelectItem value="fill">Fill — stretch to fill (may distort)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={handleSaveBranding}
                      disabled={brandingSaving}
                      data-testid="admin-branding-save"
                    >
                      <Check className="h-4 w-4" />
                      {brandingSaving ? "Saving…" : "Save branding"}
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => setBrandingDraft({
                        name: business?.name || "",
                        cover_image: business?.cover_image || "",
                        cover_image_fit: business?.cover_image_fit || "cover",
                      })}
                      disabled={brandingSaving}
                      data-testid="admin-branding-reset"
                    >
                      Reset changes
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-[24px] border-border bg-card">
                <CardContent className="space-y-3 p-5">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Live preview</p>
                    <Badge variant="outline">Customer menu hero</Badge>
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-border bg-secondary/40">
                    <div className="relative aspect-[3/1] w-full bg-secondary">
                      {brandingDraft.cover_image ? (
                        <img
                          src={brandingDraft.cover_image}
                          alt="Cover preview"
                          className="absolute inset-0 h-full w-full"
                          style={{ objectFit: brandingDraft.cover_image_fit || "cover" }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          data-testid="admin-branding-preview-image"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                          <ImagePlus className="mr-2 h-5 w-5" />
                          No cover image yet
                        </div>
                      )}
                    </div>
                    <div className="space-y-1 p-4">
                      <p className="font-['Space_Grotesk'] text-xl font-semibold leading-tight" data-testid="admin-branding-preview-name">
                        {brandingDraft.name || "Business name"}
                      </p>
                      <p className="text-sm text-muted-foreground">Browse our menu and place your order</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tip: changes only take effect for customers after you click <span className="font-semibold">Save branding</span>.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="links" className="space-y-4">
            <SectionHeader
              eyebrow="Share with the client"
              title="Role login links"
              description="Copy and send these links to the restaurant. The business code will already be filled in."
            />
            {(() => {
              const slug = business?.slug || "";
              const origin = typeof window !== "undefined" ? window.location.origin : "";
              const staffUrl = `${origin}/staff?business=${encodeURIComponent(slug)}`;
              const kitchenUrl = `${origin}/kitchen?business=${encodeURIComponent(slug)}`;
              const barUrl = `${origin}/bar?business=${encodeURIComponent(slug)}`;
              const runnerUrl = `${origin}/runner?business=${encodeURIComponent(slug)}`;
              const ownerUrl = `${origin}/owner?business=${encodeURIComponent(slug)}`;
              const retailUrl = `${origin}/retail?business=${encodeURIComponent(slug)}`;
              const previewTag = (tags || []).find((t) => t.is_preview) || (tags || []).find((t) => t.type === "menu" && !t.table_number);
              const menuUrl = previewTag
                ? `${origin}/api/menu/scan?tag=${encodeURIComponent(previewTag.code)}`
                : "";
              return (
                <div className="grid gap-4 lg:grid-cols-2">
                  <ShareableLinkCard
                    icon={Store}
                    title="Owner / Manager login"
                    description="Restaurant owner or manager — full dashboard, analytics, and settings."
                    href={ownerUrl}
                    badge="Owner / Manager"
                    testIdPrefix="admin-link-owner"
                  />
                  <ShareableLinkCard
                    icon={ClipboardList}
                    title="Staff login"
                    description="Front-of-house team — tables, orders, receipts."
                    href={staffUrl}
                    badge="Staff"
                    testIdPrefix="admin-link-staff"
                  />
                  <ShareableLinkCard
                    icon={ChefHat}
                    title="Kitchen login"
                    description="Kitchen team — incoming tickets and mark-as-ready."
                    href={kitchenUrl}
                    badge="Kitchen"
                    testIdPrefix="admin-link-kitchen"
                  />
                  <ShareableLinkCard
                    icon={Wine}
                    title="Bar login"
                    description="Bar team — incoming drink tickets and mark-as-ready."
                    href={barUrl}
                    badge="Bar"
                    testIdPrefix="admin-link-bar"
                  />
                  <ShareableLinkCard
                    icon={Truck}
                    title="Runner login"
                    description="Runners — see ready orders and deliver to tables."
                    href={runnerUrl}
                    badge="Runner"
                    testIdPrefix="admin-link-runner"
                  />
                  <ShareableLinkCard
                    icon={Store}
                    title="Retail login"
                    description="Retail counter — POS-style sales, cash & card payments, tickets to Kitchen/Bar."
                    href={retailUrl}
                    badge="Retail"
                    testIdPrefix="admin-link-retail"
                  />
                  {menuUrl ? (
                    <ShareableLinkCard
                      icon={QrCode}
                      title="Customer menu (preview)"
                      description={`Opens the menu without binding to a specific table — handy for testing the customer flow.`}
                      href={menuUrl}
                      badge="Customer"
                      testIdPrefix="admin-link-menu"
                    />
                  ) : null}                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="access" className="space-y-4">
            {/* ── Staff accounts (mirror of owner Staff & Access) ───── */}
            <SectionHeader eyebrow="Access" title="Staff" description="View, edit, reset PIN, disable or delete any user account for this business — same controls the owner has." />
            <Card className="rounded-[24px] border-border bg-card" data-testid="admin-staff-card">
              <CardContent className="space-y-3 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{adminUsers.length} {adminUsers.length === 1 ? "user" : "users"}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-xl" onClick={loadAdminUsers} data-testid="admin-staff-refresh">Refresh</Button>
                    <Button size="sm" className="rounded-xl" onClick={() => setAdminUserDialog({ open: true, mode: "create", id: "", values: { first_name: "", last_name: "", username: "", pin: "", role: "waiter", active: true, allowed_sections: null } })} data-testid="admin-staff-add">+ Add staff</Button>
                  </div>
                </div>
                {adminUsers.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead className="hidden sm:table-cell">Username</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {adminUsers.map((u) => (
                          <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                            <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                            <TableCell className="hidden text-xs text-muted-foreground sm:table-cell">{u.username}</TableCell>
                            <TableCell className="text-xs capitalize">{(u.role || "").replace(/_/g, " ")}</TableCell>
                            <TableCell className="text-center"><Badge variant={u.active ? "default" : "secondary"} className="rounded-md text-[10px]">{u.active ? "Active" : "Disabled"}</Badge></TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs" onClick={() => setAdminUserDialog({ open: true, mode: "edit", id: u.id, values: { first_name: u.first_name || "", last_name: u.last_name || "", username: u.username || "", pin: "", role: u.role || "waiter", active: !!u.active, allowed_sections: Array.isArray(u.allowed_sections) ? u.allowed_sections : null } })} data-testid={`admin-user-edit-${u.id}`}>Edit</Button>
                              <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs" onClick={() => setAdminPinDialog({ open: true, user: u, pin: "" })} data-testid={`admin-user-resetpin-${u.id}`}>Reset PIN</Button>
                              <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs" onClick={() => toggleAdminUserActive(u)} data-testid={`admin-user-toggle-${u.id}`}>{u.active ? "Disable" : "Enable"}</Button>
                              <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setAdminDeleteUserDialog({ open: true, user: u, pin: "", saving: false })} data-testid={`admin-user-delete-${u.id}`}>Delete</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No staff accounts yet for this business.</p>
                )}
              </CardContent>
            </Card>

            <SectionHeader eyebrow="Credentials" title="Reset access" description="Set new passwords for the owner, staff, kitchen, bar, or void. Existing sessions will be invalidated." />
            <Card className="rounded-[24px] border-border bg-card">
              <CardContent className="space-y-4 p-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="reset-kitchen-pw">New kitchen password</Label>
                    <Input id="reset-kitchen-pw" type="password" className="h-11 rounded-xl" value={accessForm.kitchen_password} onChange={(e) => setAccessForm((f) => ({ ...f, kitchen_password: e.target.value }))} data-testid="admin-reset-kitchen-password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reset-bar-pw">New bar password</Label>
                    <Input id="reset-bar-pw" type="password" className="h-11 rounded-xl" value={accessForm.bar_password} onChange={(e) => setAccessForm((f) => ({ ...f, bar_password: e.target.value }))} data-testid="admin-reset-bar-password" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="reset-void-pw">New void / manager password</Label>
                    <Input id="reset-void-pw" type="password" className="h-11 rounded-xl" value={accessForm.void_password} onChange={(e) => setAccessForm((f) => ({ ...f, void_password: e.target.value }))} placeholder="Shared password managers use to void orders" data-testid="admin-reset-void-password" />
                  </div>
                </div>
                <Button
                  className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  onClick={handleUpdateAccess}
                  disabled={!accessForm.owner_password && !accessForm.staff_password && !accessForm.kitchen_password && !accessForm.bar_password && !accessForm.void_password}
                  data-testid="admin-update-access-button"
                >
                  <Lock className="h-4 w-4" />
                  Update passwords
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Item dialog */}
      <Dialog open={itemDialog.open} onOpenChange={(open) => setItemDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>{itemDialog.mode === "create" ? "Add menu item" : "Edit menu item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input className="h-11 rounded-xl" value={itemDialog.values.name || ""} onChange={(e) => setItemDialog((d) => ({ ...d, values: { ...d.values, name: e.target.value } }))} data-testid="admin-item-name" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea className="rounded-xl" value={itemDialog.values.description || ""} onChange={(e) => setItemDialog((d) => ({ ...d, values: { ...d.values, description: e.target.value } }))} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Price (R)</Label>
                <Input type="number" min="0" step="0.01" className="h-11 rounded-xl" value={itemDialog.values.price ?? ""} onChange={(e) => setItemDialog((d) => ({ ...d, values: { ...d.values, price: e.target.value } }))} data-testid="admin-item-price" />
              </div>
              <div className="space-y-2">
                <Label>VAT %</Label>
                <Input type="number" min="0" max="100" step="0.1" className="h-11 rounded-xl" value={itemDialog.values.vat_percent ?? 15} onChange={(e) => setItemDialog((d) => ({ ...d, values: { ...d.values, vat_percent: e.target.value } }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={itemDialog.values.category_id || ""} onValueChange={(v) => setItemDialog((d) => ({ ...d, values: { ...d.values, category_id: v } }))}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {menu.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={itemDialog.values.department || ""} onValueChange={(v) => setItemDialog((d) => ({ ...d, values: { ...d.values, department: v } }))}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="admin-item-department-trigger">
                  <SelectValue placeholder={departments.length ? "Select department" : "Create a department first"} />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}{d.route_to_kitchen ? "  ·  Kitchen" : ""}{d.route_to_bar ? "  ·  Bar" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {departments.length
                  ? "Items in a department flagged 'routes to kitchen' appear on the Kitchen Dashboard."
                  : "Add departments under the Departments tab before assigning items."}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-item-notes-mode">Custom notes <span className="text-xs font-normal text-muted-foreground">(controls who can add a free-form note when ordering)</span></Label>
              <Select value={itemDialog.values.notes_mode || "off"} onValueChange={(value) => setItemDialog((d) => ({ ...d, values: { ...d.values, notes_mode: value } }))}>
                <SelectTrigger id="admin-item-notes-mode" className="h-11 rounded-xl" data-testid="admin-item-notes-mode-trigger">
                  <SelectValue placeholder="Off" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off — no custom notes</SelectItem>
                  <SelectItem value="staff">Staff only — waiter can add notes</SelectItem>
                  <SelectItem value="all">Staff &amp; customers — anyone can add notes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Highlight as popular item</p>
                <p className="text-xs text-muted-foreground">Adds a green "Popular" badge on the customer menu.</p>
              </div>
              <Switch checked={!!itemDialog.values.featured} onCheckedChange={(c) => setItemDialog((d) => ({ ...d, values: { ...d.values, featured: c } }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setItemDialog({ open: false, mode: "create", values: {} })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={handleSaveItem} data-testid="admin-save-item-button">
              <Check className="h-4 w-4" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag dialog */}
      <Dialog open={tagDialog.open} onOpenChange={(open) => setTagDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>{tagDialog.mode === "create" ? "Add tag" : "Edit tag"}</DialogTitle>
            <DialogDescription>Each tag stores this restaurant's menu URL when written to a physical NFC sticker.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Label</Label>
              <Input className="h-11 rounded-xl" value={tagDialog.values.label || ""} onChange={(e) => setTagDialog((d) => ({ ...d, values: { ...d.values, label: e.target.value } }))} placeholder="e.g., Front door, Bar counter, Table 5" data-testid="admin-tag-label" />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Inactive tags will not redirect customers.</p>
              </div>
              <Switch
                checked={tagDialog.values.active !== false}
                onCheckedChange={(checked) => setTagDialog((d) => ({ ...d, values: { ...d.values, active: checked } }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setTagDialog({ open: false, mode: "create", values: {} })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={handleSaveTag} data-testid="admin-save-tag-button">
              <Check className="h-4 w-4" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NfcWriteDialog tag={nfcWriteTag} onClose={() => setNfcWriteTag(null)} />

      {/* Department dialog */}
      <Dialog open={departmentDialog.open} onOpenChange={(open) => setDepartmentDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>{departmentDialog.mode === "create" ? "Add department" : "Edit department"}</DialogTitle>
            <DialogDescription>Departments group menu items by station and decide what shows on the Kitchen Dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                className="h-11 rounded-xl"
                value={departmentDialog.values.name || ""}
                onChange={(e) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, name: e.target.value } }))}
                placeholder="e.g., Kitchen, Bar, Coffee station"
                data-testid="admin-department-name-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Routes to kitchen</p>
                <p className="text-xs text-muted-foreground">If on, items in this department appear on the Kitchen Dashboard.</p>
              </div>
              <Switch
                checked={!!departmentDialog.values.route_to_kitchen}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, route_to_kitchen: checked } }))}
                data-testid="admin-department-route-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Routes to bar</p>
                <p className="text-xs text-muted-foreground">If on, items in this department appear on the Bar Dashboard.</p>
              </div>
              <Switch
                checked={!!departmentDialog.values.route_to_bar}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, route_to_bar: checked } }))}
                data-testid="admin-department-route-bar-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Also notify staff</p>
                <p className="text-xs text-muted-foreground">If on, this department's items appear on the staff "Kitchen/Bar ready" banners too, so the floor sees the full order.</p>
              </div>
              <Switch
                checked={!!departmentDialog.values.route_to_staff}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, route_to_staff: checked } }))}
                data-testid="admin-department-route-staff-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Inactive departments cannot be assigned to new items.</p>
              </div>
              <Switch
                checked={departmentDialog.values.active !== false}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, active: checked } }))}
              />
            </div>

            {/* Bulk-assign categories — Admin */}
            <div className="space-y-2 rounded-2xl border border-dashed border-border bg-secondary/30 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Bulk-assign categories</p>
                  <p className="text-xs text-muted-foreground">
                    Tick a category — every menu item in it will be moved to this department on save.
                  </p>
                </div>
                {(menu.categories || []).length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => setAdminDeptCategoryIds(adminDeptCategoryIds.length === menu.categories.length ? [] : menu.categories.map((c) => c.id))}
                    data-testid="admin-department-toggle-all-categories"
                  >
                    {adminDeptCategoryIds.length === (menu.categories || []).length ? "Clear all" : "Select all"}
                  </Button>
                ) : null}
              </div>
              {(menu.categories || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No categories yet for this business.</p>
              ) : (
                <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
                  {menu.categories.map((cat) => {
                    const checked = adminDeptCategoryIds.includes(cat.id);
                    const itemCount = (menu.items || []).filter((it) => it.category_id === cat.id).length;
                    return (
                      <label
                        key={cat.id}
                        htmlFor={`admin-dept-cat-${cat.id}`}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${checked ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10" : "border-border bg-background hover:border-[hsl(var(--accent))]/40"}`}
                      >
                        <Checkbox
                          id={`admin-dept-cat-${cat.id}`}
                          checked={checked}
                          onCheckedChange={(value) => {
                            setAdminDeptCategoryIds((current) => {
                              if (value) return current.includes(cat.id) ? current : [...current, cat.id];
                              return current.filter((id) => id !== cat.id);
                            });
                          }}
                          data-testid={`admin-department-category-${cat.id}`}
                        />
                        <span className="flex-1 truncate">{cat.name}</span>
                        <span className="text-xs text-muted-foreground">{itemCount}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {adminDeptCategoryIds.length > 0 ? (
                <p className="rounded-lg bg-emerald-600/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400">
                  On save, every item in {adminDeptCategoryIds.length} selected categor{adminDeptCategoryIds.length === 1 ? "y" : "ies"} will be reassigned to this department.
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setDepartmentDialog({ open: false, mode: "create", values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, active: true } }); setAdminDeptCategoryIds([]); }}>Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={handleSaveDepartment}
              data-testid="admin-department-save-button"
            >
              <Check className="h-4 w-4" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Admin Add/Edit User dialog ────────────────────────────── */}
      <Dialog open={adminUserDialog.open} onOpenChange={(open) => setAdminUserDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-lg" data-testid="admin-user-dialog">
          <DialogHeader>
            <DialogTitle>{adminUserDialog.mode === "edit" ? "Edit staff" : "Add staff"}</DialogTitle>
            <DialogDescription>Same fields owners use — full role + per-section permissions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>First name</Label><Input value={adminUserDialog.values.first_name} onChange={(e) => setAdminUserDialog((d) => ({ ...d, values: { ...d.values, first_name: e.target.value } }))} className="h-11 rounded-xl" data-testid="admin-user-first-name" /></div>
              <div><Label>Last name</Label><Input value={adminUserDialog.values.last_name} onChange={(e) => setAdminUserDialog((d) => ({ ...d, values: { ...d.values, last_name: e.target.value } }))} className="h-11 rounded-xl" /></div>
              <div><Label>Username</Label><Input value={adminUserDialog.values.username} onChange={(e) => setAdminUserDialog((d) => ({ ...d, values: { ...d.values, username: e.target.value } }))} className="h-11 rounded-xl" data-testid="admin-user-username" /></div>
              {adminUserDialog.mode === "create" ? (
                <div><Label>PIN (4–10 chars)</Label><Input type="password" value={adminUserDialog.values.pin} onChange={(e) => setAdminUserDialog((d) => ({ ...d, values: { ...d.values, pin: e.target.value } }))} className="h-11 rounded-xl" data-testid="admin-user-pin" /></div>
              ) : null}
            </div>
            <div>
              <Label>Role</Label>
              <Select value={adminUserDialog.values.role} onValueChange={(v) => setAdminUserDialog((d) => ({ ...d, values: { ...d.values, role: v } }))}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="admin-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{["owner","manager","shift_leader","supervisor","waiter","runner"].map((r) => (<SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g," ")}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            {["owner","manager","shift_leader","supervisor"].includes(adminUserDialog.values.role) ? (() => {
              const ROLE_DEFAULT = { owner: ["overview","operations","staff_access","menu","finance","reports","inventory","settings","ai_assistant"], manager: ["overview","operations","staff_access","menu","finance","reports","inventory","settings"], shift_leader: ["overview","operations","staff_access","reports","inventory"], supervisor: ["overview","operations"] };
              const ALL = [["overview","Overview"],["operations","Operations"],["staff_access","Staff & Access"],["menu","Menu"],["finance","Finance"],["reports","Reports"],["inventory","Inventory"],["settings","Settings"],["ai_assistant","AI Assistant"]];
              const current = Array.isArray(adminUserDialog.values.allowed_sections) && adminUserDialog.values.allowed_sections.length ? adminUserDialog.values.allowed_sections : (ROLE_DEFAULT[adminUserDialog.values.role] || []);
              const isCustom = Array.isArray(adminUserDialog.values.allowed_sections);
              return (
                <div className="rounded-2xl border border-border bg-secondary/40 p-3">
                  <div className="flex items-center justify-between"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Sections this user can see</p>{isCustom ? <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setAdminUserDialog((d) => ({ ...d, values: { ...d.values, allowed_sections: null } }))}>Use role defaults</Button> : null}</div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {ALL.map(([v, l]) => {
                      const checked = current.includes(v);
                      return (<label key={v} className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1.5 text-xs cursor-pointer"><Checkbox checked={checked} onCheckedChange={(c) => setAdminUserDialog((d) => { const base = Array.isArray(d.values.allowed_sections) ? d.values.allowed_sections : (ROLE_DEFAULT[d.values.role] || []); const n = new Set(base); if (c) n.add(v); else n.delete(v); return { ...d, values: { ...d.values, allowed_sections: Array.from(n) } }; })} data-testid={`admin-user-section-${v}`} /><span>{l}</span></label>);
                    })}
                  </div>
                </div>
              );
            })() : adminUserDialog.values.role === "owner" ? (<div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3"><p className="text-[11px] text-amber-800 dark:text-amber-200">Owner — full access. Not customisable.</p></div>) : null}
            <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 px-3 py-2"><Label className="cursor-pointer">Active</Label><Switch checked={!!adminUserDialog.values.active} onCheckedChange={(v) => setAdminUserDialog((d) => ({ ...d, values: { ...d.values, active: v } }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setAdminUserDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitAdminUser} data-testid="admin-user-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reset-PIN dialog ──────────────────────────────────────── */}
      <Dialog open={adminPinDialog.open} onOpenChange={(open) => setAdminPinDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-sm" data-testid="admin-pin-dialog">
          <DialogHeader>
            <DialogTitle>Reset PIN</DialogTitle>
            <DialogDescription>Set a new PIN for {adminPinDialog.user ? `${adminPinDialog.user.first_name} ${adminPinDialog.user.last_name}` : "this user"}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>New PIN (4–10 chars)</Label>
            <Input type="password" value={adminPinDialog.pin} onChange={(e) => setAdminPinDialog((d) => ({ ...d, pin: e.target.value }))} className="h-11 rounded-xl" data-testid="admin-pin-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setAdminPinDialog({ open: false, user: null, pin: "" })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={resetAdminUserPin} data-testid="admin-pin-submit">Set new PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete user dialog — Void Manager Password gated ─────── */}
      <Dialog open={adminDeleteUserDialog.open} onOpenChange={(open) => setAdminDeleteUserDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="admin-user-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete user</DialogTitle>
            <DialogDescription>
              {adminDeleteUserDialog.user ? (
                <>This permanently removes <strong>{adminDeleteUserDialog.user.first_name} {adminDeleteUserDialog.user.last_name}</strong> ({String(adminDeleteUserDialog.user.role || "").replace(/_/g, " ")}). Disable them instead if you only want to revoke access temporarily.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              This action cannot be undone. Past audit entries are preserved.
            </div>
            <div>
              <Label htmlFor="admin-delete-user-pin">Void Manager Password</Label>
              <Input
                id="admin-delete-user-pin"
                type="password"
                inputMode="text"
                maxLength={72}
                className="h-11 rounded-xl tracking-widest"
                value={adminDeleteUserDialog.pin}
                onChange={(e) => setAdminDeleteUserDialog((d) => ({ ...d, pin: e.target.value }))}
                placeholder="••••"
                data-testid="admin-user-delete-pin"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setAdminDeleteUserDialog({ open: false, user: null, pin: "", saving: false })}>Cancel</Button>
            <Button variant="destructive" className="rounded-xl" disabled={adminDeleteUserDialog.saving || !adminDeleteUserDialog.pin} onClick={submitAdminDeleteUser} data-testid="admin-user-delete-confirm">
              <Trash2 className="h-4 w-4" />
              {adminDeleteUserDialog.saving ? "Deleting…" : "Delete user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </main>
  );
}
